import type { InterfaceLinkDto } from '@/schemas/chip';
import type { Rotation } from '@/models/data/Rotation';
import type { TileType } from '@/models/TileTypes';
import type { TileMeta } from '@/models/tiles/TileDefinition';
import { type Side } from '@/schemas/common';
import type { ChipId } from '@/types/ids';

export interface ClipboardTileEntry {
  /** Source identity is used only to rebind references within this pasted selection. */
  sourceTileId?: string;
  tileType: TileType;
  relativeLocation: { x: number; y: number };
  meta: TileMeta;
  state?: unknown;
  interfaceLink?: InterfaceLinkDto;
}

export interface ClipboardConnectionPoint {
  x: number;
  y: number;
  side: Side;
}

export interface ClipboardConnection {
  a: ClipboardConnectionPoint;
  b: ClipboardConnectionPoint;
}

export interface ClipboardEdgeHint {
  relativeLocation: { x: number; y: number };
  side: Side;
}

export interface ClipboardChipInstance {
  bundleWidth?: number;
  rotation?: Rotation;
  chipId: ChipId;
  relativeOrigin: { x: number; y: number };
  localIndex: number;
}

export interface ClipboardData {
  version: 1;
  operation?: 'copy' | 'cut';
  /** Optional template anchor relative to the clipboard bounding box. */
  origin?: { x: number; y: number };
  width: number;
  height: number;
  tiles: ClipboardTileEntry[];
  connections: ClipboardConnection[];
  disconnected?: ClipboardConnection[];
  edgeHints: ClipboardEdgeHint[];
  chipInstances: ClipboardChipInstance[];
}

export const CLIPBOARD_DATA_PREFIX = 'digitiles:';

export function serializeClipboardData(data: ClipboardData): string {
  return CLIPBOARD_DATA_PREFIX + JSON.stringify(data);
}

export function isClipboardDataString(text: string): boolean {
  return text.startsWith(CLIPBOARD_DATA_PREFIX);
}

export const CLIP_CHIP_PREFIX = '__clip_chip_';

export function makeClipChipRef(index: number): string {
  return `${CLIP_CHIP_PREFIX}${index}`;
}

export function parseClipChipRef(ref: string): number | null {
  if (!ref.startsWith(CLIP_CHIP_PREFIX)) return null;
  const index = parseInt(ref.slice(CLIP_CHIP_PREFIX.length), 10);
  return Number.isNaN(index) ? null : index;
}

export function buildConnectedSidesMap(data: ClipboardData): Map<string, Set<Side>> {
  const map = new Map<string, Set<Side>>();

  const addSide = (x: number, y: number, side: Side) => {
    const key = `${x},${y}`;
    let set = map.get(key);
    if (!set) {
      set = new Set();
      map.set(key, set);
    }
    set.add(side);
  };

  for (const conn of data.connections) {
    addSide(conn.a.x, conn.a.y, conn.a.side);
    addSide(conn.b.x, conn.b.y, conn.b.side);
  }

  for (const hint of data.edgeHints) {
    addSide(hint.relativeLocation.x, hint.relativeLocation.y, hint.side);
  }

  return map;
}
